import isEqual from 'lodash/isEqual'
import { useState, useCallback } from 'react'
// @mui
import Card from '@mui/material/Card'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import { alpha } from '@mui/material/styles'

// routes
import { paths } from 'src/routes/paths'

import { RouterLink } from 'src/routes/components'
// types
import { IUserTableFilterValue } from 'src/types/user'
// components
import Iconify from 'src/components/iconify'
import { useSettingsContext } from 'src/components/settings'
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs'

import Datagrid from 'src/components/datagrid'

//
import { Helmet } from 'react-helmet-async'

import axiosInstance from 'src/utils/axios'
import { useSnackbar } from 'notistack'

import useEvents from 'src/service/events.service'

import { useRouter } from 'src/routes/hook'
import { IEventTableFilters } from 'src/types/events'
import EventTableToolbar from './table-user/event-table-toolbar'
import EventTableFiltersResult from './table-user/event-table-filters-result'
import EventTableRow from './table-user/event-table-row'

export const USER_STATUS_OPTIONS = [
  { value: 'true', label: 'Ativos' },
  { value: 'false', label: 'Desativados' },
]

const STATUS_OPTIONS = [{ value: 'all', label: 'Todos' }, ...USER_STATUS_OPTIONS]

const TABLE_HEAD = [
  { id: 'name', label: 'Nome', width: 220 },
  { id: 'local', label: 'Local', width: 160 },
  { id: 'date', label: 'Data do Evento', width: 140 },
  { id: 'managers', label: 'Vendedores', width: 120 },
  { id: 'status', label: 'Status', width: 100 },
  { id: '', width: 200 },
]

const defaultPagination = {
  page: 1,
  perPage: 8,
  sort: {
    createdAt: 'desc',
  },
}
const defaultFilters: IEventTableFilters = {
  active: 'all',
  search: '',
}
export default function EventsListView() {
  const [pagination, setPagination] = useState(defaultPagination)
  const [filters, setFilters] = useState<IEventTableFilters>(defaultFilters)

  const { isLoading, isFetching, data, refetch } = useEvents(pagination, filters)

  const settings = useSettingsContext()

  const { enqueueSnackbar } = useSnackbar()

  const router = useRouter()

  const canReset = !isEqual(defaultFilters, filters)

  const notFound = (!data?.data.length && canReset) || !data?.data.length

  const handleFilters = useCallback((name: string, value: IUserTableFilterValue) => {
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }, [])

  const handleDeleteRow = useCallback(
    async (id: string) => {
      try {
        await axiosInstance.delete(`/event/${id}`)

        enqueueSnackbar('Evento removido com sucesso !')
        refetch()
      } catch (error) {
        if (error?.message) {
          enqueueSnackbar(error?.message, {
            variant: 'error',
          })
        } else {
          enqueueSnackbar('Erro ao remover evento !', {
            variant: 'error',
          })
        }
      }
    },
    [enqueueSnackbar, refetch],
  )

  const handleDeleteRows = useCallback(
    async (selectedValues: string[]) => {
      try {
        selectedValues.map(async (id) => {
          await axiosInstance.delete(`/event/${id}`)
        })

        enqueueSnackbar('Evento removido com sucesso !')
        refetch()
      } catch (error) {
        if (error?.message) {
          enqueueSnackbar(error?.message, {
            variant: 'error',
          })
        } else {
          enqueueSnackbar('Erro ao remover evento !', {
            variant: 'error',
          })
        }
      }
    },
    [enqueueSnackbar, refetch],
  )

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.events.edit(id))
    },
    [router],
  )

  const handleFilterStatus = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      handleFilters('active', newValue)
    },
    [handleFilters],
  )

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters)
  }, [])

  return (
    <>
      <Helmet>
        <title>Eventos - Listagem</title>
      </Helmet>

      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <CustomBreadcrumbs
          heading='Eventos'
          links={[{ name: 'Início', href: paths.dashboard.root }, { name: 'Eventos' }]}
          action={
            <Button
              component={RouterLink}
              href={paths.dashboard.events.create}
              variant='contained'
              startIcon={<Iconify icon='mingcute:add-line' />}
            >
              Novo Evento
            </Button>
          }
          sx={{
            mb: { xs: 3, md: 5 },
          }}
        />

        <Card>
          <Tabs
            value={filters.active}
            onChange={handleFilterStatus}
            sx={{
              px: 2.5,
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {STATUS_OPTIONS.map((tab) => (
              <Tab key={tab.value} iconPosition='end' value={tab.value} label={tab.label} />
            ))}
          </Tabs>

          <EventTableToolbar filters={filters} onFilters={handleFilters} />

          {canReset && (
            <EventTableFiltersResult
              filters={filters}
              onFilters={handleFilters}
              onResetFilters={handleResetFilters}
              results={data?.data.length || 0}
              sx={{ p: 2.5, pt: 0 }}
            />
          )}

          <Datagrid
            data={data?.data || []}
            notFound={notFound}
            isLoading={isLoading || isFetching}
            headLabel={TABLE_HEAD}
            refetch={refetch}
            total={data?.total || 0}
            setPagination={setPagination}
            pagination={pagination}
            handleDeleteRows={handleDeleteRows}
            ItemContent={EventTableRow}
            handleDeleteRow={handleDeleteRow}
            handleEditRow={handleEditRow}
          />
        </Card>
      </Container>
    </>
  )
}
